/*
 * The decisions behind useDeployWatcher, kept free of React, the router and
 * the DOM so they can be tested on their own.
 */

/** How often an open tab asks whether a new build has gone out. */
export const DEPLOY_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/* sessionStorage keys. Per tab, so one tab's reload never stops another's. */
export const DEPLOY_RELOAD_KEY = 'sk:deploy-reload-for';
/* The version the "new version" toast was raised for; set, the tab never raises it again. */
export const DEPLOY_TOAST_KEY = 'sk:deploy-toast-for';

export type DeployAction = 'none' | 'reload' | 'defer';

/** The `version` out of a `/version.json` body, or null for anything else (an HTML 404, a bad deploy). */
export function readDeployedVersion(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const { version } = body as { version?: unknown };
  return typeof version === 'string' && version.length > 0 ? version : null;
}

/**
 * True when `pathname` is one of `patterns`. A pattern matches its own path
 * exactly (a trailing slash aside); one ending in `/*` also matches everything
 * under it. So `/` is only the root, never the whole app.
 */
export function matchesPath(
  pathname: string,
  patterns: readonly string[],
): boolean {
  const path = trimSlash(pathname);

  return patterns.some(pattern => {
    if (pattern.endsWith('/*')) {
      const base = trimSlash(pattern.slice(0, -2));
      return path === base || path.startsWith(`${base}/`);
    }

    return path === trimSlash(pattern);
  });
}

function trimSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

export type DeployDecisionInput = {
  /** The version baked into the running bundle. */
  running: string | undefined;
  /** The version `/version.json` reports, or null when it could not be read. */
  deployed: string | null;
  /** The page is a login, auth or public page, where nothing is lost by reloading. */
  onReloadSafePage: boolean;
  /** The user has typed into a field on this page, or has one focused. */
  editing: boolean;
  /** The deployed version this tab already reloaded for, from sessionStorage. */
  reloadedFor: string | null;
};

/**
 * What to do about the version `/version.json` reports:
 * - `none`: same version, nothing readable, or this tab already reloaded for
 *   it once (the page it got back was still old, so reloading again would loop);
 * - `reload`: a login, auth or public page with nothing typed into it;
 * - `defer`: anywhere else, or mid-form: say so, and reload when the user asks.
 */
export function decideDeployAction({
  running,
  deployed,
  onReloadSafePage,
  editing,
  reloadedFor,
}: DeployDecisionInput): DeployAction {
  if (!running || !deployed || deployed === running) {
    return 'none';
  }

  if (reloadedFor === deployed) {
    return 'none';
  }

  return onReloadSafePage && !editing ? 'reload' : 'defer';
}

/*
 * Browsers word a failed `import()` differently:
 * Chrome "Failed to fetch dynamically imported module", Firefox "error loading
 * dynamically imported module", Safari "Importing a module script failed".
 * Vite's own preload failure is "Unable to preload CSS for ...".
 */
const CHUNK_ERROR_PATTERN =
  /dynamically imported module|importing a module script failed|unable to preload css/i;

/** True for the error a lazy chunk throws when the deploy it came from is gone. */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';

  return CHUNK_ERROR_PATTERN.test(message);
}

/**
 * sessionStorage, or null where it is unavailable (privacy modes, sandboxed
 * frames). Without it the loop guards cannot hold, so callers do not reload.
 */
export function getSessionStorage(): Storage | null {
  try {
    const storage = window.sessionStorage;
    const probe = '__sk_probe__';
    storage.setItem(probe, probe);
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

export function readFlag(storage: Storage | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** Writes the flag and reports whether it stuck, so a reload only happens behind a guard that holds. */
export function writeFlag(
  storage: Storage | null,
  key: string,
  value: string,
): boolean {
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(key, value);
    return storage.getItem(key) === value;
  } catch {
    return false;
  }
}

export type StaleChunkWatchOptions = {
  /** Called for each stale chunk seen. It tells the user; it never reloads on its own. */
  onStaleChunk: () => void;
};

/**
 * Watches for a lazy chunk that fails to load, which is what an old tab hits
 * after a deploy removes the chunks it was built against. It never reloads:
 * a sidebar click must stay an in-app navigation, so the caller says a new
 * version is out and the user reloads when they choose. Returns a cleanup.
 */
export function installStaleChunkWatch({
  onStaleChunk,
}: StaleChunkWatchOptions): () => void {
  /*
   * Vite fires this for every failed preload and lazy import it wraps. The
   * error is left to propagate, so the page that asked for the chunk (see
   * lazyPage) can show what happened.
   */
  const onPreloadError = () => {
    onStaleChunk();
  };

  /* A bare import() that Vite did not wrap rejects without that event. */
  const onRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      onStaleChunk();
    }
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
